import { FullPageWrapperContext } from 'global/cnr/contexts/FullPageWrapperContext';
import { useAppUserAccess } from 'global/cnr/contexts/useHelpers/useAppUserAccess';
import ItemMenuSquares, { _imsTypes } from 'global/wrappers/ItemMenuSquares';
import _ from 'lodash';
import { createContext, useContext, useEffect, useMemo, useReducer, useState } from 'react';
import { Icon, Label, Sidebar } from 'semantic-ui-react';
import { ParentContext } from '../../../global/cnr/contexts/ParentContext';
import { convertHelpers } from "../../../global/common/convert";
import { DimmerPending } from '../../../global/components/alerts/DimmerWrap';
import UiSaveButtons from '../../../global/components/buttons/UiSaveButtons';
import DropdownOptionsMenu from "../../../global/components/forms/elements/DropdownOptionsMenu";
import DataSidebar from '../../../global/components/swipers/DataSidebar';
import JsonViewer from '../../../global/components/viewers/JsonViewer';
import TreeViewer from "../../../global/components/viewers/TreeViewer";
import StepWizard from '../../../global/components/wizard/StepWizard';
import { gEnums } from '../../../global/enums/globalEnums';
import { openExternal } from '../../../global/viewSettings/helpers/settingsLinks';
import FullPageWrapper from '../../../global/wrappers/FullPageWrapper';
import Wrapper, { wrapperTypes } from '../../../global/wrappers/Wrapper';
import WrapperVertCols from '../../../global/wrappers/WrapperVertCols';
import { SportsDistrictContext } from '../cnr/contexts/SportsDistrictContext';
import { SportsSeasonContext } from '../cnr/contexts/SportsSeasonContext';
import { createMediaSeasonHandlers, createMediaSeasonInitialState, createMediaSeasonReducer } from "../cnr/reducers/CreateMediaSeasonReducer";
import { createPlayoffHandlers, createPlayoffInitialState, createPlayoffReducer } from '../cnr/reducers/CreatePlayoffReducer';
import { createScheduleTypes } from '../cnr/reducers/CreateSportsReducer';
import { sportsShowTypes } from '../cnr/reducers/SportsSeasonReducer';
import { _sportCollections } from '../cnr/reducers/SportsSidebarReducer';
import { tournamentTypes } from '../cnr/reducers/SportsTournamentReducer';
import MatchGrid from '../elements/matches/MatchGrid';
import MatchSquares, { _msTypes } from '../elements/matches/MatchSquares';
import LeagueStandings, { _standingsTypes } from '../elements/standings/LeagueStandings';
import TeamList from '../elements/teams/TeamList';
import TeamSquares from '../elements/teams/TeamSquares';
import { usePlayoffInfo } from '../useHelpers/usePlayoffInfo';
import { _allowSportsUpdates, useCreateSports, useSeasonalData } from '../useHelpers/useSeasonals';
import { CreateSportsContext } from './CreateSports';
import CreateSeeds, { seedTypes } from './subs/CreateSeeds';
import CreateTournamentSchedule from './subs/CreateTournamentSchedule';
import TeamMatchCompare from './subs/TeamMatchCompare';
import { dataFix } from 'global/common/dataFix';
import { sportsHelpers } from '../cnr/reducers/reducerHelpers/sportsHelpers';

const stepTypes = {
  playoffLevels: 'playoffLevels',
  // playoffDates: 'playoffDates',
  playoffTeams: 'playoffTeams',
  playoffSeeds: 'playoffSeeds',
  playoffBrackets: 'playoffBrackets',
}


export const CreatePlayoffContext = createContext();

const CreatePlayoff = ({ handleCancel, fromDashboard, fromSeed }) => {

  const _asat = gEnums.availableSubActionTypes

  const parentContext = useContext(ParentContext);
  const { states, fns } = parentContext ?? {}
  const { app_state, paps_state } = states ?? {}
  const { mainDb } = app_state ?? {}
  const { database_fns } = fns ?? {}
  const { pathViews } = paps_state ?? {}

  const isMobileDevice = false // _.get(app_state, 'currentEnvironment.platform.isMobileDevice', false)

  const appUserAccess = useAppUserAccess()
  let { isSuperAdminDeveloper } = appUserAccess ?? {}
  // isSuperAdminDeveloper = false

  const fullPageWrapperContext = useContext(FullPageWrapperContext)
  const { fullPageWrapper_state } = fullPageWrapperContext ?? {}
  const { wideModeOn } = fullPageWrapper_state ?? {}
  const _useWizard = wideModeOn ? false : true

  const sportsDistrictContext = useContext(SportsDistrictContext)
  const { sportsDistrict_state } = sportsDistrictContext ?? {}
  const { district_data } = sportsDistrict_state ?? {}
  const { sports } = district_data ?? {}

  // sportsSeasonContext
  const sportsSeasonContext = useContext(SportsSeasonContext)
  const { sportsSeason_state } = sportsSeasonContext ?? {}
  const { currentSeason_info, details_info, matches_info, sportsSeason_datas, sportsButtonProps, parentDataKeys, sportPermissions, links_score } = sportsSeason_state ?? {}
  const { sportsKey, _sportsYearAbr, _sportsAbr, _sportsModeKey, _sportsModeSport, latestSeason } = currentSeason_info ?? {}
  const { selectedSeason } = sportsSeason_datas ?? {}
  const { playoffMatches: playoffMatches_key, matches_seasonal: pk_matches_seasonal } = parentDataKeys ?? {}
  const { matches_playoffs: playoffMatches_season } = matches_info ?? {}
  const playoffMatches_seasonLevels = playoffMatches_season ? _.groupBy(playoffMatches_season, 'levels') : {}

  const _sportsYear_abr = _sportsYearAbr || `${_sportsAbr}_${latestSeason}`

  // createSportsContext
  const createSportsContext = useContext(CreateSportsContext);
  const { createSports_state } = createSportsContext ?? {}
  const {
    currentSport,
    item_state,
    subActionType,
    teams_info,
    teams,
  } = createSports_state ?? {}

  const _latestSeason = selectedSeason ? selectedSeason : latestSeason



  const init_state = {
    teams, // for standings YES
    item_state,
    details_info,
  }

  const [createPlayoff_state, createPlayoff_dispatch] = useReducer(createPlayoffReducer, init_state, createPlayoffInitialState)
  const createPlayoff_handlers = useMemo(
    () => createPlayoffHandlers(createPlayoff_dispatch, createPlayoff_state),
    [createPlayoff_dispatch]
  );

  const { seededTeams, playoff_info, playoff_matchInfo, showPlayoffMatchLocation } = createPlayoff_state ?? {}
  const { playoffLevel, playoffLevelTeams, playoffTeams, playoffTeamCompares, playoffDates_ready, playoffDates_current, parentDataKey } = playoff_info ?? {}
  const { tournamentMatches } = playoff_matchInfo ?? {}
  const _playoffLevelReady = playoffDates_ready && playoffLevel && playoffDates_ready[playoffLevel]
  const teams_playoffGroup = playoffLevelTeams?.[playoffLevel]

  const init_state_ms = {
    parentDataKeys,
    pathViews,
    database_fns,
    sportsKey,
    sportPermissions,
    _sportsAbr,
    sports,
    teams_info
  }

  const [createMediaSeason_state, createMediaSeason_dispatch] = useReducer(createMediaSeasonReducer, init_state_ms, createMediaSeasonInitialState)
  const createMediaSeason_handlers = createMediaSeasonHandlers(createMediaSeason_dispatch, createMediaSeason_state)
  const { mediaSeason } = createMediaSeason_state

  const _currentSport = _sportsModeKey ? _.startCase(_sportsModeKey) : currentSport

  const _currents = {
    teams: playoffLevelTeams && playoffLevelTeams[playoffLevel] ? _.size(playoffLevelTeams[playoffLevel]) : 0,
    matches: tournamentMatches && tournamentMatches[playoffLevel] ? _.size(tournamentMatches[playoffLevel]) : 0,
    matchesSeason: playoffMatches_seasonLevels && playoffMatches_seasonLevels[playoffLevel] ? _.size(playoffMatches_seasonLevels[playoffLevel]) : 0,
    dates_current: playoffDates_current?.currentCount,
    dates_needed: playoffDates_current?.currentNeeded,
    datesReady: playoffDates_ready?.[playoffLevel]
  }

  const _parentDataKeys = parentDataKeys

  const { matches: pdk_matches_seasonal } = _parentDataKeys ?? {}

  const _parentDataKey = playoffMatches_key ? playoffMatches_key : parentDataKey

  const steps = [
    stepTypes.playoffLevels,
    stepTypes.playoffTeams,
    stepTypes.playoffSeeds,
    stepTypes.playoffBrackets
  ]

  // local state   
  const [mode_right, setMode_right] = useState()
  const [levels, setLevels] = useState()
  const [step, setStep] = useState({ index: 0, name: steps[0] })
  const stepCount = steps.length

  const { updateSeasonalPlayoffInfo, savePlayoffTournament, getSportsYearCollectionItem, sportsYearCollectionItem, updating, confirmation } = usePlayoffInfo({ sportsKey, _latestSeason, sportPermissions, _sportsYearAbr: _sportsYear_abr })
  const { getSeasonalData } = useSeasonalData()
  const { getFullPlayoffInfo } = useCreateSports()

  // get the playoff info from the database
  useEffect(() => {
    const cb = (results, fk) => {
      createPlayoff_handlers.handleSet_playoffInfo(results, fk)
    }
    getSeasonalData(sportsKey, _latestSeason, cb, null, { altSeasonalName: '_playoffs' })
    getSportsYearCollectionItem()
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, []);

  useEffect(() => {
    if (playoffLevelTeams) {
      const z = dataFix.removeEmpties(playoffLevelTeams)
      setLevels(Object.keys(z).sort().reverse())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [playoffLevelTeams]);

  // get the playoff info from the database
  useEffect(() => {
    if (showPlayoffMatchLocation) {
      setMode_right(sportsShowTypes.matchLocation)
    } else {
      setMode_right()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [showPlayoffMatchLocation]);

  useEffect(() => {
    if (mediaSeason) {
      console.log('createMediaSeason_state', createMediaSeason_state)
      // createSportsSeason_handlers.handleMedia_ammendSeasonalFromMedia(mediaSeasonalData_current, matches_info)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [mediaSeason]);

  const [showGrid, setShowGrid] = useState()
  const [selectedSeededTeam, setSelectedSeededTeam] = useState()

  const handleMode_none = () => setMode_right()
  const handleOpenDb = () => openExternal.firebaseDb(pathViews, _sportCollections._matches_seasonal, pk_matches_seasonal, false, false, false, ['matches', 'playoffs'])
  const handleOpenDbp = () => openExternal.firebaseDb(pathViews, '_playoffs', _parentDataKey, false, false, false, ['matches'])
  const handleOpenUrl_playoffs = () => openExternal.firebaseDbDirect(pathViews, [_sportCollections._playoffs, parentDataKey])
  const handleOpenUrl_seasonMatches = () => openExternal.firebaseDbDirect(pathViews, [_sportCollections._matches_seasonal, pdk_matches_seasonal])
  const handleSelectedSeededTeam = (item) => setSelectedSeededTeam(item)
  const handleSet_modeRight = (opts) => setMode_right(opts ? opts.modeRight : step.name)
  const handleShowGrid = () => setShowGrid(!showGrid)

  const handleUpdate_seasonalPlayoffInfo = () => {
    updateSeasonalPlayoffInfo({ createPlayoff_state })
  }

  const handleSave_playoffTournament = () => {
    savePlayoffTournament({ playoff_matchInfo, playoffLevel, mode_right, cbAfter: handleMode_none })
  }

  const handleClick_getSeasonalMedia = () => {
    createMediaSeason_handlers.handleStartUpdate()
    createMediaSeason_handlers.handleGet_mediaSeason(_latestSeason, _sportsModeSport, links_score, _sportsAbr)
  }

  const validateComponent = () => {
    switch (step.name) {
      case stepTypes.playoffLevels:
        return playoffLevel ? true : false
      case stepTypes.playoffTeams:
        return playoffTeams && playoffTeams.length > 0 ? true : false
      case stepTypes.playoffSeeds:
        switch (subActionType) {
          case _asat.createTournament:
            return seededTeams && Object.keys(seededTeams).length > 0 ? true : false
          case _asat.createPlayoff:
            return playoffLevelTeams && Object.keys(playoffLevelTeams).length > 0 ? true : false
          default:
            return false
        }
      // case stepTypes.playoffDates:
      //   return playoffDatesAreReady

      case stepTypes.playoffBrackets:
        return true
      default:
        break;
    }
  }

  const allowBack = step.index > 0
  const allowForward = (step.index < (stepCount - 1) && validateComponent())

  const playoff_levels = () => {
    console.log('levels', levels)
    if (!levels) return;
    const hc = (item) => createPlayoff_handlers.handleAmmend_level(item)
    return <ItemMenuSquares
      menuItems={levels}
      imsType={_imsTypes.inline}
      activeItem={playoffLevel}
      onClick={hc}
    />
  }

  const select_playoffTeams = () => <LeagueStandings
    menuDisplayType={gEnums.menuDisplayTypes.menuButtons}
    standingType={_standingsTypes.playoff}
    forPlayoff={true}
    playoffLevel={playoffLevel}
  />

  const create_seeds = () => <CreateSeeds
    createScheduleType={createScheduleTypes.playoff}
    seedType={seedTypes.playoff}
    menuDisplayType={gEnums.menuDisplayTypes.menuButtons}
    selectedSeededTeam={selectedSeededTeam}
    handleSelectedTeam={handleSelectedSeededTeam}
  />


  const create_tournamentSchedule = () => <CreateTournamentSchedule
    createScheduleType={createScheduleTypes.playoff}
    tournamentType={tournamentTypes.newPlayoff}
    asGrid={true}
    menuDisplayType={gEnums.menuDisplayTypes.menuButtons}
  />

  const copyToClipboard = () => {
    const dataItems = _.sortBy(teams_playoffGroup, 'seed')
    sportsHelpers.copyTeamDataToClipboard(dataItems, ['seed', 'name'])
  }

  const wrapper_opps = () => <Wrapper
    header={<div className='header-flex'>
      <div>{`Common Opponents: ${selectedSeededTeam?.name}`}</div>
      <div><Icon name='delete' onClick={() => handleSelectedSeededTeam(null)} />    </div>
    </div>}
    content={<TeamSquares teams={teams} selectedTeam={selectedSeededTeam} noMid={false} />}
    wrapperType={wrapperTypes.padded}
  ></Wrapper>

  // const buttons_column = () => {
  //   const clickOptions = { clickType: step.name }
  //   switch (step.name) {
  //     case stepTypes.playoffTeams:
  //       return [
  //         { oc: handleSet_modeRight, ...sportsButtonProps, color: sportPermissions.any && sportPermissions.playoffTeams ? 'blue' : 'olive', caption: 'Update ' + _.startCase(step.name), clickOptions },
  //         { oc: handleOpenDb, ...sportsButtonProps, color: 'orange', caption: 'Open Database (Matches)', fl: true },
  //         { oc: handleOpenDbp, ...sportsButtonProps, color: 'orange', caption: 'Open Database (Playoff Matches)', fl: true }
  //       ]
  //     case stepTypes.playoffSeeds:
  //       return [{ oc: handleSet_modeRight, ...sportsButtonProps, color: sportPermissions.any && sportPermissions.playoffSeeds ? 'green' : 'olive', caption: 'Update ' + _.startCase(step.name), clickOptions }]
  //     // case stepTypes.playoffDates:
  //     //   return [{ oc: handleSet_modeRight, ...sportsButtonProps, color: sportPermissions.any && sportPermissions.playoffDates ? 'green' : 'olive', caption: 'Update ' + _.startCase(step.name), clickOptions }]
  //     case stepTypes.playoffBrackets:
  //       return [{ oc: handleSave_playoffTournament, icon: 'save', color: sportPermissions.any && sportPermissions.playoffBrackets ? 'green' : 'olive', caption: 'Update ' + _.startCase(step.name), clickOptions }]
  //     default:
  //       break;
  //   }
  // }

  const wrapper_content = (content, header, wt) => {
    return <Wrapper
      header={header}
      content={content}
      // footer={_buttons && <UiSaveButtons others={_buttons} />}
      wrapperType={wt || wrapperTypes.padded}
      wrapperCss={{ container: 'app-dark-mode' }}
    />
  }

  const wrapper_sidebar = (content) => <Sidebar.Pushable style={{ overflow: 'hidden' }}>
    {sidebar_right(mode_right ? true : false)}
    <Sidebar.Pusher dimmed={mode_right ? true : false} className={'h100'}>
      {wrapper_content(content)}
    </Sidebar.Pusher>
  </Sidebar.Pushable>

  const stepCaption = () => {
    const name = _.startCase(step.name)
    switch (step.name) {
      // case stepTypes.playoffDates:
      //   return playoffDates_selected ? name + ' (' + playoffDates_selected.length + ')' : name
      case stepTypes.playoffTeams:
        return name
      default:
        return name
    }
  }

  const stepCaption2 = () => {
    switch (step.name) {
      // case stepTypes.playoffDates:
      //   return <div style={{ textAlign: 'center', paddingBottom: '1em' }}>{`Count: ${currentCount} of ${currentNeeded}`}</div>
      default:
      // nothing
    }
  }

  const buttons_footer_right = () => {
    let caption = _.startCase(mode_right)
    switch (mode_right) {
      case sportsShowTypes.createPlayoffMatches:
        caption += ' (' + playoffLevel + ')'
        break;
      default:
      // nothing
    }
    const btns = []
    btns.push({
      oc: handleSave_playoffTournament,
      caption: caption,
      icon: 'save',
      color: _allowSportsUpdates.playoffMatches && sportPermissions?.any ? 'green' : 'teal',
      opts: { modeRight: mode_right },
    })
    return btns
  }

  const footer_right = () => <UiSaveButtons others={buttons_footer_right()} />

  const content_right = () => {
    let _content;
    let _contentData;
    let _header = mode_right;

    switch (mode_right) {
      case sportsShowTypes.matchLocation:
        _content = <TeamList
          handleCancel={createPlayoff_handlers.handleShow_playoffMatchLocation}
          handleSelect={createPlayoff_handlers.handleAmmend_matchLocation}
          contentOnly={true}
        />
        break;
      case sportsShowTypes.viewSchedules:
        _content = <TeamMatchCompare sportsShowType={mode_right} inViewer={true} />
        break;
      case sportsShowTypes.compareTeams:
        _content = <TeamMatchCompare sportsShowType={mode_right} inViewer={true} />
        break;
      case sportsShowTypes.createPlayoffMatches:
        const playoffMatches = getFullPlayoffInfo(playoff_matchInfo, playoffLevel)
        _contentData = playoffMatches
        _content = `Update ${_.size(playoffMatches)} matches for ${playoffLevel}?`
        break;
      case sportsShowTypes.createAllPlayoffMatches:
        const allPlayoffMatches = getFullPlayoffInfo(playoff_matchInfo, null, true)
        const _allPlayoffMatches = {}
        if (allPlayoffMatches) {
          _.forEach(allPlayoffMatches, (mg, mgk) => {
            _allPlayoffMatches[mgk] = convertHelpers.groupBy_keys(mg, 'poolKey')
          })
        }
        // _content = <WrapperVertCols
        //   mvc={'1-2'}
        //   split={true}
        // >
        //   <JsonViewer json={_allPlayoffMatches} name={step.name} />
        //   <TreeViewer data={_allPlayoffMatches} scNames={true} />
        // </WrapperVertCols>
        _contentData = _allPlayoffMatches
        _content = <TreeViewer data={_allPlayoffMatches} scNames={true} />
        break;
      case stepTypes.playoffTeams:
        _contentData = playoffTeams
        _content = <JsonViewer json={playoffTeams} name={step.name} />
        break;
      case stepTypes.playoffSeeds:
        _contentData = playoffTeams
        _content = <JsonViewer json={playoffTeams} name={step.name} />
        break;
      // case stepTypes.playoffDates:
      //   _contentData = playoffTeams
      //   _content = <JsonViewer json={playoffTeams} name={step.name} />
      //   break;
      case stepTypes.playoffBrackets:
        _contentData = playoffTeams
        _content = <JsonViewer json={playoffTeams} />
        break;
      default:
        break;
    }
    return <Wrapper
      header={_.startCase(_header)}
      content={_content}
      contentData={_contentData}
      footer={footer_right()}
      wrapperType={wrapperTypes.padded}
    />
  }

  const sidebar_right = (visible) => <DataSidebar visible={visible} content={visible && content_right()} onHide={handleMode_none} direction={'right'} />

  const lblIcon = (caption, key, hasData, oc, rm, ocdb) => <Label className='lbl-key' onClick={() => { oc && oc(rm) }}>
    <div>
      <Icon name={hasData ? 'check circle outline' : 'circle outline'} color={hasData ? 'green' : 'grey'} />
      {caption}
      <Icon name={ocdb && hasData ? 'database' : 'ban'} color={ocdb && hasData ? 'green' : 'grey'} onClick={(e) => {
        e.stopPropagation()
        ocdb && ocdb(e)
      }} />
    </div>
    <div>{key ? key : 'No Parent Keys'}</div>
  </Label>


  const lblIcon_count = (caption, count, hasData, oc, rm) => <Label className='lbl-key-count' onClick={() => { oc && oc(rm) }}>
    <div>
      <Icon name={hasData ? 'check circle outline' : 'circle outline'} color={hasData ? 'green' : 'grey'} />
      <div>{caption}</div>
      <div>{count}</div>
    </div>
  </Label>

  const header_top = () => {
    const lbls = []
    if (isSuperAdminDeveloper) {
      lbls.push(lblIcon('Current Year', selectedSeason, true))
      lbls.push(lblIcon('SYCI', sportsYearCollectionItem?._itemKey, sportsYearCollectionItem?._itemKey ? true : false, null, null, handleOpenUrl_playoffs))
      lbls.push(lblIcon('Playoffs', parentDataKey, parentDataKey ? true : false, null, null, handleOpenUrl_playoffs))
      lbls.push(lblIcon_count('Playoff Matches', _currents.matches, true, handleSet_modeRight, sportsShowTypes.createPlayoffMatches, handleOpenUrl_seasonMatches))
      lbls.push(lblIcon_count('Season Playoff Matches', _currents.matchesSeason, _currents.matchesSeason ? true : false, handleSet_modeRight, sportsShowTypes.createPlayoffMatches, handleOpenUrl_seasonMatches))

    }
    return <div className={'header-flex'}>
      {isSuperAdminDeveloper && <div>{'Create Playoff (' + _currentSport + ')'}</div>}
      {!isSuperAdminDeveloper && <div>{'Playoff Wizard'}</div>}
      {lbls}
    </div>
  }

  const header_column = (name, caption, count, count2) => <div className={'header-flex'}>
    <div className='ln-flex'>
      <Label size='tiny' color='black'>{playoffLevel}</Label>
      <div>{name}</div>
    </div>
    <div>
      {count2 ? <Label size='tiny' color='blue' >{`${caption}: ${count} of ${count2}`}</Label> :
        <Label size='tiny' color='blue' >{`${caption}: ${count}`}</Label>}
    </div>
  </div>

  const wvc_playoffs = () => {
    if (showGrid) {
      return <MatchGrid levelKey={playoffLevel} />
    } else {
      return <WrapperVertCols mvc={'playoffs-4'} applySwiper={false}>
        {wrapper_content(playoff_levels(), 'Levels')}
        {playoffLevel && wrapper_content(select_playoffTeams(), 'Standings')}
        {playoffLevel && create_seeds()}
        {/* {playoffLevel && wrapperContent_sidebar(teamSquares(), 'Teams')} */}
        {playoffLevel && selectedSeededTeam && wrapper_opps()}
        {playoffLevel && !selectedSeededTeam && wrapper_content(create_tournamentSchedule(), header_column('Playoff Bracket', 'Max Bracket', _currents?.matches))}
        {!playoffLevel && <div className='hwcc'>Select Level</div>}
      </WrapperVertCols>
    }

  }

  const ddb = (menuButtons, value, caption, icon, direction, color, disabled) => <DropdownOptionsMenu
    menuButtons={menuButtons}
    value={value}
    caption={caption}
    icon={icon}
    direction={direction}
    color={color}
    disabled={disabled}
    upward={true}
    ignoreCaption={true}
  />

  const buttons_footer = () => {

    const sbs_left = []
    const sbs_right = []
    const btns_media = []

    const playoffTeamComparesCount = playoffTeamCompares && playoffTeamCompares.length

    const btns_left = []
    const btns_right = []

    if (playoffLevel) {
      btns_left.push({ oc: handleShowGrid, caption: 'Grid', color: showGrid ? 'blue' : 'grey', icon: 'grid layout', fl: true })

      if (isSuperAdminDeveloper) {
        btns_right.push({ oc: handleUpdate_seasonalPlayoffInfo, caption: _.startCase('updateSeasonalPlayoffInfo') + ' (' + playoffLevel + ')', icon: 'save', color: !_allowSportsUpdates.seasonals ? 'teal' : 'green', opts: { modeRight: sportsShowTypes.createPlayoffMatches } })
        btns_right.push({ oc: handleSet_modeRight, caption: _.startCase(sportsShowTypes.createPlayoffMatches) + ' (' + playoffLevel + ')', icon: 'arrow right', disabled: !_playoffLevelReady, color: !_allowSportsUpdates.playoffMatches ? 'teal' : 'green', opts: { modeRight: sportsShowTypes.createPlayoffMatches } })
        btns_right.push({ oc: copyToClipboard, caption: 'Copy (' + playoffLevel + ')', icon: 'copy', color: 'blue' })
      } else {
        btns_right.push({ oc: copyToClipboard, caption: 'Copy (' + playoffLevel + ')', icon: 'save', color: 'blue' })
      }

    }

    sbs_left.push(<UiSaveButtons others={btns_left} />)
    sbs_right.push(<UiSaveButtons others={btns_right} />)

    if (isSuperAdminDeveloper && _latestSeason) {
      btns_media.push({ oc: handleClick_getSeasonalMedia, icon: 'arrow right', caption: 'Create Media Season (' + _latestSeason + ')' })
    }

    return { sbs_left, sbs_right, btns_media }

  }

  const footer_main = () => {
    const { sbs_left, sbs_right, btns_media } = buttons_footer()
    const disabled_media = false
    return <div className={'header-flex'}>
      <div>
        {sbs_left}
        {btns_media.length > 0 && ddb(btns_media, null, 'Media Options', 'list ul', null, 'purple', disabled_media)}
      </div>
      <div>
        {sbs_right}
      </div>
    </div>
  }

  const content = () => {
    if (_useWizard && 1 === 3) {
      switch (step.name) {
        case stepTypes.playoffLevels:
          return wrapper_sidebar(playoff_levels())
        case stepTypes.playoffTeams:
          return wrapper_sidebar(select_playoffTeams())
        case stepTypes.playoffSeeds:
          return wrapper_sidebar(create_seeds())
        // case stepTypes.playoffDates:
        //   return wrapper_sidebar(create_scheduleDates())
        case stepTypes.playoffBrackets:
          return wrapper_sidebar(create_tournamentSchedule())
        default:
          break;
      }
    } else {
      return wrapper_sidebar(
        <Wrapper
          header={header_top()}
          content={wvc_playoffs()}
          footer={footer_main()}
          updating={updating}
          confirmation={confirmation}
          wrapperType={wrapperTypes.paddedHeaderAndFooter}
        />
      )
    }
  }

  const fpw = () => <FullPageWrapper
    content={content()}
    fullMode={true}
    handleCancel={handleCancel}
  />

  const stepWizard = () => <StepWizard
    allowBack={allowBack}
    allowForward={allowForward}
    content={content()}
    handleCancel={handleCancel}
    fullMode={fromDashboard ? true : false}
    setStep={setStep}
    step={step}
    stepCaption={stepCaption()}
    stepCaption2={stepCaption2()}
    steps={steps}
    topperCaption={'Create Playoff'}
    topperCaption2={_parentDataKey}
    updating={updating}
    useTopper={true}
  ></StepWizard>

  const content_playoff = () => {
    // if (fromDashboard) {
    //   return fpw()
    // } else {
    //   return content()
    // }
    if (_useWizard && isMobileDevice) {
      return stepWizard()
    } else {
      if (fromDashboard) {
        return content()
      } else {
        return fpw()
      }
    }
  }

  const wrapper = () => <CreatePlayoffContext.Provider value={{ createPlayoff_state, createPlayoff_handlers }}>
    {content_playoff()}
  </CreatePlayoffContext.Provider>

  return step && playoff_info ? wrapper() : <DimmerPending />

}

export default CreatePlayoff